import { useEffect, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { SectionCard, Row, fmtDate } from '../../shared/Panel';
import { clientDay } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';

/**
 * Styl jazdy z eco-trucka na karcie kierowcy (08.10.2026).
 *
 * Decyzja użytkownika: „możemy zapisywać te dane, ale na razie nas nie
 * interesują; ewentualnie można je wyświetlić w profilu kierowcy”. Sekcja
 * jest więc zwinięta i dociąga dane dopiero po rozwinięciu
 * (`GET /drivers/:id/eco-truck-score`). To informacja pomocnicza — nie
 * wpływa na rekomendacje pojazdów ani na koszt dolotu.
 *
 * Jednostek swagger dostawcy nie podaje, więc liczby stoją bez jednostek,
 * z dopiskiem „jednostka wg eco-trucka” — nie zgadujemy, czy czas jazdy to
 * godziny, czy minuty.
 */

export interface DriverScoreRow {
  periodStart: string;
  periodEnd: string;
  fetchedAt: string;
  driverStyleScore: number | null;
  averageFuelConsumption: number | null;
  totalDistanceTravelled: number | null;
  totalDriveTime: number | null;
  totalIdleTime: number | null;
  vehicleCount: number | null;
}

export interface DriverScoreView {
  enabled: boolean;
  latest: DriverScoreRow | null;
  previous: DriverScoreRow[];
  noDataReason: string | null;
  lastRunAt: string | null;
}

export async function fetchDriverScore(driverId: number | string): Promise<DriverScoreView> {
  const { data } = await axiosNest.get(`/drivers/${driverId}/eco-truck-score`);
  return data as DriverScoreView;
}

/** Liczba z eco-trucka bez jednostki — brak to „—”, nie zero. */
export const scoreValue = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : plNumber(v);

/**
 * Tydzień oceny po polsku: „28.09.2026 – 04.10.2026”. Koniec okresu to
 * chwila rozpoczęcia następnego tygodnia, więc ostatni dzień liczymy
 * milisekundę wcześniej.
 */
export const scorePeriod = (r: Pick<DriverScoreRow, 'periodStart' | 'periodEnd'>): string => {
  const end = new Date(new Date(r.periodEnd).getTime() - 1).toISOString();
  return `${clientDay(r.periodStart)} – ${clientDay(end)}`;
};

const UNIT_NOTE = 'jednostka wg eco-trucka';

export const EcoTruckScore = ({ driverId }: { driverId: number | string }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<DriverScoreView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || view !== null) return;
    let cancelled = false;
    setError(null);
    fetchDriverScore(driverId)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch((e) => {
        if (!cancelled) setError(toErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, view, driverId]);

  const latest = view?.latest ?? null;

  return (
    <SectionCard title="Styl jazdy (eco-truck)">
      <Button size="small" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? 'Zwiń' : 'Pokaż'}
      </Button>
      {open && (
        <Box sx={{ mt: 1 }} data-testid="eco-truck-score">
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!error && view === null && (
            <Typography variant="body2" color="text.secondary">
              Wczytywanie…
            </Typography>
          )}
          {view !== null && latest === null && (
            <Typography variant="body2">
              Brak danych — {view.noDataReason ?? 'eco-truck nie oddał oceny'}.
            </Typography>
          )}
          {latest !== null && (
            <>
              <Table size="small">
                <TableBody>
                  <Row label="Tydzień">{scorePeriod(latest)}</Row>
                  <Row label="Ocena stylu jazdy">{scoreValue(latest.driverStyleScore)}</Row>
                  <Row label="Średnie zużycie paliwa">{scoreValue(latest.averageFuelConsumption)}</Row>
                  <Row label="Przejechany dystans">{scoreValue(latest.totalDistanceTravelled)}</Row>
                  <Row label="Czas jazdy">{scoreValue(latest.totalDriveTime)}</Row>
                  <Row label="Czas postoju">{scoreValue(latest.totalIdleTime)}</Row>
                  <Row label="Liczba pojazdów">{scoreValue(latest.vehicleCount)}</Row>
                  <Row label="Pobrano">{fmtDate(latest.fetchedAt)}</Row>
                </TableBody>
              </Table>
              {view !== null && view.previous.length > 0 && (
                <Table size="small" sx={{ mt: 1 }} aria-label="Poprzednie oceny">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tydzień</TableCell>
                      <TableCell align="right">Ocena</TableCell>
                      <TableCell align="right">Dystans</TableCell>
                      <TableCell align="right">Czas jazdy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {view.previous.map((p) => (
                      <TableRow key={p.periodStart}>
                        <TableCell>{scorePeriod(p)}</TableCell>
                        <TableCell align="right">{scoreValue(p.driverStyleScore)}</TableCell>
                        <TableCell align="right">{scoreValue(p.totalDistanceTravelled)}</TableCell>
                        <TableCell align="right">{scoreValue(p.totalDriveTime)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Informacja pomocnicza z telematyki — nie wpływa na rekomendacje pojazdów ani na koszt
            dolotu. Liczby jak podaje eco-truck ({UNIT_NOTE}): dostawca nie opisuje jednostek.
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
};
